// ─────────────────────────────────────────────
//  Design tokens — single source of truth (D-03, D-06)
//  Color, spacing, radius, shadow, typography. Plain JS
//  object on the CRA stack — no Tailwind / CSS-in-JS (D-04).
//  Clinical-blue palette (D-01, D-01a, D-02) — UAT Gap 7.
//  Soft blue-gray ground with white cards and crisp navy borders.
//  Structured so a dark mode could be layered later (deferred).
// ─────────────────────────────────────────────
//
// WCAG AA Contrast — computed values (Plan 13, 2026-06-10)
// Pair                              Ratio   Pass AA (4.5:1 normal / 3:1 large)
// text.primary  (#0d0f1a) / base    16.37   ✓
// text.primary  (#0d0f1a) / card    19.08   ✓
// text.primary  (#0d0f1a) / raised  15.14   ✓
// text.secondary (#4a5280) / base    6.41   ✓
// text.secondary (#4a5280) / card    7.47   ✓
// text.secondary (#4a5280) / raised  5.93   ✓
// text.muted    (#565d82) / base     5.49   ✓
// text.muted    (#565d82) / card     6.40   ✓
// text.muted    (#565d82) / raised   5.07   ✓
// ─────────────────────────────────────────────

const tokens = {
  color: {
    surface: {
      base: "#e9eef5",   // soft blue-gray page ground (clinical-blue restyle, Plan 13)
      card: "#ffffff",   // white card surface — stands off the blue ground
      raised: "#dde6f1", // blue-tinted raised/input surface
      // Error-message ground. This value was already hardcoded in Field.js,
      // LoginPage.js, GoLivePage.js and NoAccessPage.js — four copies of one
      // decision, which is a missing token rather than four mistakes. Named
      // here so error regions stay consistent and the contrast below is
      // asserted once instead of re-derived per screen.
      // ⚠️ CORRECTED 2026-08-08. This comment used to claim `accent.error`
      // (#dc2626) on this ground was **4.95:1 and passed AA**. Recomputed by
      // hand and with a WCAG calculator, it is **4.41:1 and FAILS** for normal
      // text. Four screens (Field, LoginPage, GoLivePage, NoAccessPage) were
      // consolidated onto that one sentence, so the wrong number propagated as
      // an assurance rather than as a value.
      //
      // Use **#991b1b (7.60:1)** as the ink on this ground, not accent.error.
      // accent.error remains correct as a BORDER or FILL — it is only as text
      // that it falls short. It also does not pass on surface.raised (#dde6f1),
      // so an error region must not sit on the raised surface either.
      error: "#fef2f2",
      // The AA-safe ink for text on `surface.error`. Named so no screen has to
      // re-derive it, and so the next person does not reach for accent.error.
      errorInk: "#991b1b",
    },
    border: {
      // ONE border colour, and it clears WCAG 1.4.11's 3:1 everywhere:
      // 4.04:1 on card (#ffffff) / 3.47:1 on base (#e9eef5) / 3.21:1 on raised
      // (#dde6f1). The old #b9c6d8 was 1.73 / 1.48 / 1.37 — it never reached
      // 3:1 on any surface in the app.
      //
      // ⚠️ This was briefly split into `default` (decorative) + `control`
      // (interactive), on the theory that only control boundaries owe 3:1 and
      // retinting every card was an unasked-for restyle. Measured on the
      // deployed timeline page, that split fixed NOTHING: 90 bordered controls,
      // 83 still on the failing colour, 0 on the new one. Controls here style
      // their own borders with this token inline; they do not route through
      // Field.js/Button.js, so there was nothing to inherit the fix. One token
      // that passes beats two tokens where the right one goes unused.
      default: "#6f7f9c",
    },
    text: {
      primary: "#0d0f1a",   // deep navy ink (intentional; legacy-hex sweep must not flag tokens.js)
      secondary: "#4a5280",
      muted: "#565d82",
    },
    accent: {
      primary: "#1e40af",   // navy deep blue (CTA, focus ring, logo, ready pill)
      active: "#10b981",    // green (active status, success toast border)
      closing: "#f59e0b",   // amber (closing status)
      cancelled: "#a855f7", // purple (cancelled status)
      error: "#dc2626",     // red (errors, destructive actions)
    },
    // ── The lobby palette (landing dashboard only) ──
    // The clinical-blue ground above is for WORKING screens — a board being read
    // across a room, a timeline being edited. The post-login landing is not a
    // working screen; it is the door people come through, and the brief asked
    // for hospitality warmth there.
    //
    // The reconciliation: warmth lives in the SURFACES, the brand lives in the
    // MARKS. Ink stays text.primary, the medallion and CTA stay accent.primary
    // navy. Nothing here is used outside HomePage — a warm ground under a room
    // card would read as a different product.
    //
    // WCAG AA — computed 2026-08-18 (same method as the header table above):
    //   text.primary   (#0d0f1a) / linen  (#f6f2ea)  15.8   ✓
    //   text.primary   (#0d0f1a) / plaque (#fffdf9)  18.9   ✓
    //   text.secondary (#4a5280) / linen              6.2   ✓
    //   text.secondary (#4a5280) / plaque             7.4   ✓
    //   brassInk       (#7a5c26) / linen              5.6   ✓
    //   brassInk       (#7a5c26) / plaque             6.1   ✓
    //   brass          (#9a7b3f) / plaque             3.9   ✗ for text — see below
    //
    // ⚠️ `brass` FAILS AA as normal text (3.9:1). It is a RULE and a BORDER
    // colour only. Every warm word on this page uses `brassInk`. This is the
    // same trap `surface.error` documents above, written down before it bites.
    lobby: {
      linen:    "#f6f2ea", // warm limestone page ground
      plaque:   "#fffdf9", // warm white card ("the door plaque")
      sand:     "#eae2d5", // hairline border / recessed edge
      brass:    "#9a7b3f", // the drawn rule — NOT a text colour
      brassInk: "#7a5c26", // the AA-safe warm ink for eyebrows and meta
    },
    // Light-mode status tints — the SINGLE source for every status-colored
    // surface (StatusPill, the RoomCard status bar). domain/status.js keeps the
    // labels + dark meta; these are the accessible light-board renderings.
    // text values ≥ 4.5:1 on their bg (empty was 4.1:1 before; darkened).
    status: {
      empty:     { bg: "#f0f2f5", text: "#4a5280", border: "#dde1ea" },
      ready:     { bg: "#e8edf9", text: "#1e3a8a", border: "#1e40af" },
      active:    { bg: "#e6f8f3", text: "#096e53", border: "#10b981" },
      closing:   { bg: "#fef8ec", text: "#7a4f00", border: "#f59e0b" },
      cancelled: { bg: "#f8f0fe", text: "#7b2fbe", border: "#a855f7" },
    },
  },
  space: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    "2xl": 48,
    "3xl": 64,
  },
  radius: {
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
    full: 9999,
  },
  shadow: {
    sm: "0 1px 3px rgba(13,38,76,0.10), 0 1px 2px rgba(13,38,76,0.06)",
    md: "0 4px 12px rgba(13,38,76,0.12), 0 2px 4px rgba(13,38,76,0.07)",
    lg: "0 8px 24px rgba(13,38,76,0.14), 0 4px 8px rgba(13,38,76,0.08)",
  },
  font: {
    family: '"IBM Plex Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    size: {
      label: 12,   // footnotes, timestamps, badge text, field labels, error text
      body: 14,     // form fields, list items, descriptions, button labels
      heading: 18,  // card headings, modal titles
      display: 22,  // app name / page titles
    },
    weight: {
      regular: 400,
      medium: 500,    // subtle hierarchy — roles, secondary labels
      semibold: 600,  // names, headings, emphasis short of bold
      bold: 700,
    },
    lineHeight: {
      tight: 1.2,  // display, heading
      snug: 1.4,   // label
      normal: 1.5, // body
    },
  },
};

export default tokens;
